/**
 * Client API REST pour Ember.
 * Toutes les routes passent par le proxy Vite (/api -> http://localhost:4000/api en dev).
 */

export interface Player {
  id: string;
  pseudo: string;
  intensity: number;
  optIns: {
    sex: boolean;
    emotion: boolean;
    dare: boolean;
  };
}

export type SessionMode = 'STANDARD' | 'GUESS';

export interface Session {
  id: string;
  shortCode: string;
  status: 'WAITING' | 'ACTIVE' | 'FINISHED' | 'ABANDONED';
  mode: SessionMode;
  intensityMin: number | null;
  optInSex: boolean;
  optInEmotion: boolean;
  optInDare: boolean;
  players: Player[];
}

export interface CreateSessionResponse {
  id: string;
  shortCode: string;
  status: 'WAITING' | 'ACTIVE' | 'FINISHED' | 'ABANDONED';
}

export interface JoinSessionResponse {
  session: Session;
  playerId: string;
}

export interface JoinSessionInput {
  pseudo: string;
  intensity: number;
  optIns: {
    sex: boolean;
    emotion: boolean;
    dare: boolean;
  };
}

export interface PackSummary {
  slug: string;
  name: string;
  description: string;
  isFree: boolean;
  questionCount: number;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    let payload: { error?: string; code?: string } = {};
    try {
      payload = await res.json();
    } catch {
      // ignore body parse errors
    }
    throw new ApiError(
      res.status,
      payload.code ?? 'UNKNOWN_ERROR',
      payload.error ?? `Erreur HTTP ${res.status}`,
    );
  }

  // 204 No Content : pas de body a parser
  if (res.status === 204) return undefined as T;

  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Admin API : routes protegees par Bearer ADMIN_TOKEN (header Authorization)
// ---------------------------------------------------------------------------

export interface AdminPack {
  id: string;
  slug: string;
  name: string;
  description: string;
  questionCount: number;
}

export interface AdminQuestion {
  id: string;
  text: string;
  intensity: number;
  optIns: { sex: boolean; emotion: boolean; dare: boolean };
  source: 'YAML' | 'ADMIN';
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

function adminHeaders(token: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function adminRequest<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      ...adminHeaders(token),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let payload: { error?: string; code?: string } = {};
    try {
      payload = await res.json();
    } catch {
      // ignore
    }
    throw new ApiError(
      res.status,
      payload.code ?? 'UNKNOWN_ERROR',
      payload.error ?? `Erreur HTTP ${res.status}`,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const adminApi = {
  listPacks: (token: string) => adminRequest<{ packs: AdminPack[] }>('/api/admin/packs', token),

  listQuestions: (token: string, packSlug: string) =>
    adminRequest<{
      pack: { id: string; slug: string; name: string };
      questions: AdminQuestion[];
    }>(`/api/admin/packs/${packSlug}/questions`, token),

  createQuestion: (
    token: string,
    input: {
      packSlug: string;
      text: string;
      intensity: number;
      optIns: { sex: boolean; emotion: boolean; dare: boolean };
    },
  ) =>
    adminRequest<{ question: AdminQuestion }>('/api/admin/questions', token, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateQuestion: (
    token: string,
    id: string,
    input: {
      text?: string;
      intensity?: number;
      optIns?: { sex: boolean; emotion: boolean; dare: boolean };
    },
  ) =>
    adminRequest<{ question: AdminQuestion }>(`/api/admin/questions/${id}`, token, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  deleteQuestion: (token: string, id: string) =>
    adminRequest<void>(`/api/admin/questions/${id}`, token, {
      method: 'DELETE',
    }),

  /**
   * Telecharge le YAML d'un pack (texte brut) :
   *   - source 'ADMIN' : juste les questions creees via l'admin
   *   - source 'ALL'   : toutes les questions du pack
   * Retourne le contenu YAML en string (pas du JSON).
   */
  exportPack: async (
    token: string,
    packSlug: string,
    source: 'ADMIN' | 'ALL' = 'ADMIN',
  ): Promise<string> => {
    const res = await fetch(`/api/admin/packs/${packSlug}/export?source=${source}`, {
      headers: adminHeaders(token),
    });
    if (!res.ok) {
      let payload: { error?: string; code?: string } = {};
      try {
        payload = await res.json();
      } catch {
        // ignore
      }
      throw new ApiError(
        res.status,
        payload.code ?? 'UNKNOWN_ERROR',
        payload.error ?? `Erreur HTTP ${res.status}`,
      );
    }
    return await res.text();
  },
};

export interface UploadMediaResponse {
  filename: string;
  mimeType: string;
  sizeBytes: number;
  type: 'PHOTO' | 'AUDIO';
  url: string;
}

export const api = {
  health: () => request<{ status: string; db: string; env: string }>('/api/health'),

  listPacks: () => request<{ packs: PackSummary[] }>('/api/packs'),

  /**
   * Upload d'un media (photo ou audio) pour une reponse Dare.
   * Le fichier est sauve sur le serveur et un filename random est retourne.
   * Ce filename doit ensuite etre passe au socket round:submit pour etre lie
   * a la Response a l'upsert.
   */
  uploadMedia: async (
    shortCode: string,
    playerId: string,
    type: 'PHOTO' | 'AUDIO',
    file: File | Blob,
  ): Promise<UploadMediaResponse> => {
    const form = new FormData();
    form.append('playerId', playerId);
    form.append('type', type);
    form.append('media', file, file instanceof File ? file.name : `upload.${type.toLowerCase()}`);

    const res = await fetch(`/api/sessions/${shortCode}/media`, {
      method: 'POST',
      body: form,
    });
    if (!res.ok) {
      let payload: { error?: string; code?: string } = {};
      try {
        payload = await res.json();
      } catch {
        // ignore body parse errors
      }
      throw new ApiError(
        res.status,
        payload.code ?? 'UPLOAD_FAILED',
        payload.error ?? `Echec de l'upload (${res.status})`,
      );
    }
    return (await res.json()) as UploadMediaResponse;
  },

  createSession: (creatorPseudo: string, packSlug?: string, mode?: SessionMode) =>
    request<CreateSessionResponse>('/api/sessions', {
      method: 'POST',
      body: JSON.stringify({
        creatorPseudo,
        ...(packSlug ? { packSlug } : {}),
        ...(mode ? { mode } : {}),
      }),
    }),

  getSession: (shortCode: string) => request<Session>(`/api/sessions/${shortCode}`),

  joinSession: (shortCode: string, input: JoinSessionInput) =>
    request<JoinSessionResponse>(`/api/sessions/${shortCode}/join`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};
